/**
 * Collab wire contract (Collab Phase 3) — the ONE shared file imported by BOTH
 * halves of the realtime system: the browser client (later phases) and the
 * Cloudflare Worker / CollabRoom DO.
 *
 * Platform-neutral by construction: no browser globals, no node:* imports, no
 * builder-core imports. Everything here must run identically under jsdom,
 * Node, and workerd — the divergence hash only works if both sides compute it
 * from the same code.
 *
 * Contents: protocol/frame constants, per-frame validators, the limits table,
 * error/close codes, `canonicalStringify` (recursive sorted keys),
 * `fnv1a64Hex`, `hashableState` (deep clone minus LOCAL_ONLY_PATHS), the
 * local-only path rules, and the shared asset-id pattern + asset header
 * contract used by the Phase 3 asset-sync routes.
 */

import { acceptKeysForIdentity } from './audit-accept-schema.js';

// ── protocol identity ───────────────────────────────────────

export const COLLAB_PROTOCOL_VERSION = 1;
export const COLLAB_SUBPROTOCOL = 'pb.collab.v1';
/** Additive capability; old clients remain compatible with unrepaired rooms. */
export const EDITABLE_ID_REPAIR_CAPABILITY = 1;
/** Token-in-subprotocol auth fallback: ['pb.collab.v1', 'pb.token.<jwt>']. */
export const COLLAB_TOKEN_SUBPROTOCOL_PREFIX = 'pb.token.';

// ── local-only state fields ─────────────────────────────────

/**
 * Document fields that are semantically VIEW/bookkeeping state and must be
 * excluded from replication AND from the divergence hash:
 *  - activePage: each collaborator keeps their own page view
 *  - updatedAt: stamped outside the patch stream by _bumpRevision
 *  - cloudVersion: per-device cloud-sync bookkeeping
 */
export const LOCAL_ONLY_PATHS = [
  'activePage',
  'updatedAt',
  'cloudVersion',
  'identityRepairLocalOrigin'
];

/** True when `path` IS a local-only path or lies under one. */
export function isLocalOnlyPath(path) {
  if (typeof path !== 'string') return false;
  return LOCAL_ONLY_PATHS.some((p) => path === p || path.startsWith(`${p}.`));
}

/** Drop patches targeting local-only paths (outbound AND inbound defense). */
export function stripLocalOnlyPatches(patches) {
  return (patches || []).filter((patch) => !isLocalOnlyPath(patch?.path));
}

// ── canonical stringify + hash ──────────────────────────────

/**
 * Deterministic JSON: object keys recursively sorted, arrays in order.
 * JSON semantics for oddballs (undefined/function object values are omitted,
 * array holes/undefined become null, non-finite numbers become null) so both
 * halves agree byte-for-byte.
 */
export function canonicalStringify(value) {
  if (value === null) return 'null';
  const type = typeof value;
  if (type === 'number') return Number.isFinite(value) ? String(value) : 'null';
  if (type === 'boolean') return value ? 'true' : 'false';
  if (type === 'string') return JSON.stringify(value);
  if (type === 'undefined' || type === 'function' || type === 'symbol') return undefined;
  if (Array.isArray(value)) {
    const parts = value.map((item) => canonicalStringify(item) ?? 'null');
    return `[${parts.join(',')}]`;
  }
  if (type === 'object') {
    const keys = Object.keys(value).sort();
    const parts = [];
    for (const key of keys) {
      const encoded = canonicalStringify(value[key]);
      if (encoded === undefined) continue;
      parts.push(`${JSON.stringify(key)}:${encoded}`);
    }
    return `{${parts.join(',')}}`;
  }
  return undefined;
}

/** 64-bit FNV-1a over the UTF-8 bytes of `str`, as 16 lowercase hex chars. */
export function fnv1a64Hex(str) {
  const bytes = new TextEncoder().encode(String(str));
  const PRIME = 0x100000001b3n;
  const MASK = 0xffffffffffffffffn;
  let hash = 0xcbf29ce484222325n;
  for (let i = 0; i < bytes.length; i += 1) {
    hash ^= BigInt(bytes[i]);
    hash = (hash * PRIME) & MASK;
  }
  return hash.toString(16).padStart(16, '0');
}

function deleteByPath(obj, path) {
  const keys = path.split('.');
  let current = obj;
  for (let i = 0; i < keys.length - 1; i += 1) {
    if (current == null || typeof current !== 'object') return;
    current = current[keys[i]];
  }
  if (current != null && typeof current === 'object') {
    delete current[keys[keys.length - 1]];
  }
}

/** Deep clone of `state` minus LOCAL_ONLY_PATHS — the hash input shape. */
export function hashableState(state) {
  const clone = structuredClone(state);
  for (const path of LOCAL_ONLY_PATHS) deleteByPath(clone, path);
  if (clone && typeof clone === 'object') {
    const acceptKeys = acceptKeysForIdentity(clone.auditAccepts);
    if (acceptKeys.length > 0) clone.auditAccepts = acceptKeys;
    else delete clone.auditAccepts;
  }
  return clone;
}

/** The divergence hash both halves exchange in hello/sync frames. */
export function stateHashOf(state) {
  return fnv1a64Hex(canonicalStringify(hashableState(state)));
}

// ── limits ──────────────────────────────────────────────────

export const LIMITS = Object.freeze({
  hello: Object.freeze({ maxBytes: 16 * 1024 }),
  seed: Object.freeze({ maxBytes: 20 * 1024 * 1024 }),
  op: Object.freeze({ maxBytes: 512 * 1024, ratePerSec: 20, burst: 40 }),
  presence: Object.freeze({ maxBytes: 4 * 1024, ratePerSec: 10 }),
  lock: Object.freeze({ ratePerSec: 5 }),
  saved: Object.freeze({ ratePerSec: 1 }),
  editorClaim: Object.freeze({ ratePerSec: 1, ttlMs: 90_000 }),
  sync: Object.freeze({ minIntervalMs: 10_000 }),
  catchupOpsPerFrame: 100,
  maxConnsPerRoom: 8,
  helloTimeoutMs: 15_000,
  lockTtlMs: 90_000,
  pingIntervalMs: 30_000
});

// ── frame + error vocabulary ────────────────────────────────

export const CLIENT_FRAMES = Object.freeze([
  'hello',
  'seed',
  'op',
  'presence',
  'lock',
  'saved',
  'claim-editor',
  'sync',
  'ping'
]);

export const SERVER_FRAMES = Object.freeze([
  'welcome',
  'seed-request',
  'seed-wait',
  'catchup',
  'op',
  'op-rejected',
  'presence',
  'lock-result',
  'lock-state',
  'peer-joined',
  'peer-left',
  'save-leader',
  'save-notify',
  'editor-changed',
  'resync',
  'error',
  'pong'
]);

export const OP_REJECT_REASONS = Object.freeze([
  'lock_held',
  'stale',
  'apply_failed',
  'too_large',
  'rate_limited',
  'not_seeded',
  'editor_claim_required',
  'identity_repair_invalid',
  'client_upgrade_required'
]);

export const CLOSE_CODES = Object.freeze({
  PROTOCOL_VIOLATION: 4400,
  MEMBERSHIP_REVOKED: 4403,
  PROJECT_DELETED: 4404,
  HELLO_TIMEOUT: 4408,
  CLIENT_UPGRADE_REQUIRED: 4406,
  ROOM_FULL: 4423,
  INTERNAL: 1011
});

// ── patch validation ────────────────────────────────────────

/**
 * Path segments that could pollute prototypes when the server materializes
 * intermediate objects (patch-utils setByPath). Validators reject them and
 * patch-utils throws on them — defense in both layers.
 */
export const FORBIDDEN_PATH_SEGMENTS = Object.freeze(['__proto__', 'constructor', 'prototype']);

const MAX_PATH_LENGTH = 500;

export function isValidPatchPath(path) {
  if (typeof path !== 'string' || path.length === 0 || path.length > MAX_PATH_LENGTH) return false;
  const segments = path.split('.');
  return segments.every(
    (segment) => segment.length > 0 && !FORBIDDEN_PATH_SEGMENTS.includes(segment)
  );
}

function isIndex(value) {
  return Number.isInteger(value) && value >= 0;
}

/** Exact absent Undo is restricted to whole optional repair companions. */
export function isEditableIdRepairAbsentPath(path) {
  return (
    typeof path === 'string' &&
    /^(?:pages\.(?:0|[1-9]\d*)\.sections\.(?:0|[1-9]\d*)|sharedSections\.(?:navigation|footer))\.(?:overrides|editableIdRepairs)$/.test(
      path
    )
  );
}

export function isEditableIdRepairAbsentPatch(patch) {
  return (
    patch?.op === 'replace' &&
    patch.absent === true &&
    patch.value === null &&
    isEditableIdRepairAbsentPath(patch.path)
  );
}

/** One serialized StateStore patch (the History.push wire shape). */
export function isValidPatch(patch) {
  if (!patch || typeof patch !== 'object' || Array.isArray(patch)) return false;
  if (!isValidPatchPath(patch.path)) return false;
  switch (patch.op) {
    case 'replace':
      return patch.absent === true ? isEditableIdRepairAbsentPatch(patch) : 'value' in patch;
    case 'insert':
      return isIndex(patch.index) && 'item' in patch;
    case 'remove':
      return isIndex(patch.index);
    case 'move':
      return isIndex(patch.from) && isIndex(patch.to);
    default:
      return false;
  }
}

export function isValidPatchList(patches) {
  return Array.isArray(patches) && patches.length > 0 && patches.every(isValidPatch);
}

// ── frame validators ────────────────────────────────────────

const MAX_ID_LENGTH = 120;
const MAX_LABEL_LENGTH = 200;
const MAX_SECTION_KEY_LENGTH = 200;
const MAX_SECTIONS_PER_OP = 50;
const MAX_HASH_LENGTH = 32;
const MAX_PENDING_OP_IDS = 200;

function optional(value, predicate) {
  return value === undefined || value === null || predicate(value);
}

function isShortString(value, max) {
  return typeof value === 'string' && value.length > 0 && value.length <= max;
}

function isSeq(value) {
  return Number.isInteger(value) && value >= 0;
}

/**
 * Per-frame validators for client→server frames. Unknown EXTRA fields are
 * ignored (forward compat); unknown frame TYPES are rejected. Byte-size and
 * rate limits are enforced separately by the server against LIMITS.
 */
export const CLIENT_FRAME_VALIDATORS = Object.freeze({
  hello(frame) {
    return (
      frame.v === COLLAB_PROTOCOL_VERSION &&
      isShortString(frame.projectId, MAX_ID_LENGTH) &&
      optional(frame.lastSeq, isSeq) &&
      optional(frame.roomEpoch, (v) => isShortString(v, MAX_ID_LENGTH)) &&
      optional(frame.stateHash, (v) => isShortString(v, MAX_HASH_LENGTH)) &&
      optional(frame.cloudVersion, isSeq) &&
      optional(frame.deviceLabel, (v) => isShortString(v, 80)) &&
      optional(
        frame.capabilities,
        (v) =>
          v &&
          typeof v === 'object' &&
          !Array.isArray(v) &&
          optional(v.editableIdRepair, (version) => version === EDITABLE_ID_REPAIR_CAPABILITY)
      ) &&
      optional(
        frame.pendingClientOpIds,
        (ids) =>
          Array.isArray(ids) &&
          ids.length <= MAX_PENDING_OP_IDS &&
          ids.every((id) => isShortString(id, MAX_ID_LENGTH)) &&
          new Set(ids).size === ids.length
      )
    );
  },
  seed(frame) {
    return (
      frame.state != null &&
      typeof frame.state === 'object' &&
      !Array.isArray(frame.state) &&
      isSeq(frame.baseCloudVersion) &&
      isShortString(frame.stateHash, MAX_HASH_LENGTH)
    );
  },
  op(frame) {
    return (
      isShortString(frame.clientOpId, MAX_ID_LENGTH) &&
      isSeq(frame.baseSeq) &&
      optional(frame.identityRepair, (version) => version === EDITABLE_ID_REPAIR_CAPABILITY) &&
      optional(frame.roomEpoch, (value) => isShortString(value, MAX_ID_LENGTH)) &&
      isValidPatchList(frame.forward) &&
      optional(frame.label, (v) => typeof v === 'string' && v.length <= MAX_LABEL_LENGTH) &&
      Array.isArray(frame.sections) &&
      frame.sections.length <= MAX_SECTIONS_PER_OP &&
      frame.sections.every((s) => isShortString(s, MAX_SECTION_KEY_LENGTH)) &&
      typeof frame.structural === 'boolean' &&
      (frame.pageId === '*' || isShortString(frame.pageId, MAX_ID_LENGTH))
    );
  },
  presence(frame) {
    return (
      optional(
        frame.cursor,
        (c) =>
          c &&
          typeof c === 'object' &&
          Number.isFinite(c.x) &&
          Number.isFinite(c.y) &&
          isShortString(c.viewportName, MAX_ID_LENGTH)
      ) &&
      optional(
        frame.selection,
        (s) =>
          s &&
          typeof s === 'object' &&
          isShortString(s.instanceId, MAX_ID_LENGTH) &&
          optional(s.eid, (v) => isShortString(v, MAX_ID_LENGTH))
      ) &&
      optional(frame.editingSectionKey, (v) => isShortString(v, MAX_SECTION_KEY_LENGTH)) &&
      optional(frame.pageId, (v) => isShortString(v, MAX_ID_LENGTH))
    );
  },
  lock(frame) {
    return (
      (frame.action === 'acquire' || frame.action === 'release') &&
      isShortString(frame.sectionKey, MAX_SECTION_KEY_LENGTH)
    );
  },
  saved(frame) {
    return Number.isInteger(frame.version) && frame.version >= 1 && isSeq(frame.atSeq);
  },
  'claim-editor'(frame) {
    return frame.action === 'claim' || frame.action === 'takeover' || frame.action === 'release';
  },
  sync(frame) {
    return isSeq(frame.seq) && isShortString(frame.stateHash, MAX_HASH_LENGTH);
  },
  ping() {
    return true;
  }
});

/**
 * Validate a decoded client frame. Returns `{ok:true, t}` or
 * `{ok:false, error}`. Callers gate byte size / rate / ordering separately.
 */
export function validateClientFrame(frame) {
  if (!frame || typeof frame !== 'object' || Array.isArray(frame)) {
    return { ok: false, error: 'not_an_object' };
  }
  const validator = CLIENT_FRAME_VALIDATORS[frame.t];
  if (!validator) return { ok: false, error: 'unknown_frame_type' };
  try {
    return validator(frame) ? { ok: true, t: frame.t } : { ok: false, error: `invalid_${frame.t}` };
  } catch {
    return { ok: false, error: `invalid_${frame.t}` };
  }
}

// ── section lock keys ───────────────────────────────────────

export const SHARED_LOCK_KEYS = Object.freeze({
  navigation: 'shared:navigation',
  footer: 'shared:footer'
});

export function sectionLockKey(instanceId) {
  return `s:${instanceId}`;
}

// ── asset sync contract (Phase 3 routes) ────────────────────

/**
 * Shared asset-id pattern — MUST stay compatible with the renderer's
 * `PB_ASSET_RE` (`pb-asset://([a-f0-9-]+)`, iframe-renderer.js): the renderer
 * only parses lowercase hex/hyphen ids, so the route only accepts those.
 */
export const ASSET_ID_PATTERN = '[a-f0-9-]{1,80}';
export const ASSET_ID_RE = new RegExp(`^${ASSET_ID_PATTERN}$`);

export const MAX_ASSET_BYTES = 25 * 1024 * 1024;

/** Attribution metadata fields carried as URI-encoded X-PB-Asset-* headers. */
export const ASSET_META_HEADERS = Object.freeze({
  hash: 'X-PB-Asset-Hash',
  filename: 'X-PB-Asset-Filename',
  source: 'X-PB-Asset-Source',
  author: 'X-PB-Asset-Author',
  authorUrl: 'X-PB-Asset-Author-Url'
});

/** Max length of each decoded metadata value. */
export const ASSET_META_VALUE_MAX = 300;

/**
 * Header names for CORS: clients SEND these on PUT (preflight allow-list) and
 * the server RETURNS them on GET (expose list). Same names both directions.
 */
export const ASSET_CORS_HEADERS = Object.freeze(Object.values(ASSET_META_HEADERS));
